import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import type { CrisisResource } from '../types'

// Fallback resources in case API is unavailable (offline-first)
const FALLBACK_RESOURCES: CrisisResource[] = [
  {
    id: '988',
    name: '988 Suicide & Crisis Lifeline',
    description: 'Free, confidential support 24/7',
    phone: '988',
    sms: null,
    url: null,
    type: 'hotline',
    country_code: 'US'
  },
  {
    id: 'crisis-text',
    name: 'Crisis Text Line',
    description: 'Text HOME to 741741',
    phone: null,
    sms: '741741',
    url: null,
    type: 'text',
    country_code: 'US'
  },
  {
    id: 'international',
    name: 'International Association for Suicide Prevention',
    description: 'Find a crisis center in your country',
    phone: null,
    sms: null,
    url: 'https://www.iasp.info/resources/Crisis_Centres/',
    type: 'online',
    country_code: 'INT'
  }
]

export default function Crisis() {
  const { get } = useApi()
  const [resources, setResources] = useState<CrisisResource[]>(FALLBACK_RESOURCES)
  const [showBreathing, setShowBreathing] = useState(false)
  const [breathPhase, setBreathPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale')
  const [breathCount, setBreathCount] = useState(0)

  // Load resources from API, fall back to hardcoded if offline
  useEffect(() => {
    const loadResources = async () => {
      try {
        const result = await get<{ resources: CrisisResource[] }>('/api/crisis-resources')
        if (result.resources && result.resources.length > 0) {
          setResources(result.resources)
        }
      } catch {
        // Use fallback resources - already set
      }
    }
    loadResources()
  }, [get])

  // Breathing exercise timer
  useEffect(() => {
    if (!showBreathing) return

    const phases = [
      { name: 'inhale' as const, duration: 4000 },
      { name: 'hold' as const, duration: 4000 },
      { name: 'exhale' as const, duration: 6000 }
    ]

    let currentPhaseIndex = 0
    setBreathPhase(phases[0].name)

    const runPhase = () => {
      const phase = phases[currentPhaseIndex]
      setBreathPhase(phase.name)

      return setTimeout(() => {
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length
        if (currentPhaseIndex === 0) {
          setBreathCount(c => c + 1)
        }
        runPhase()
      }, phase.duration)
    }

    const timeout = runPhase()

    return () => clearTimeout(timeout)
  }, [showBreathing])

  const handleCall988 = () => {
    window.location.href = 'tel:988'
  }

  return (
    <div className="page" style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="page-content" style={{
        paddingTop: '1rem',
        paddingBottom: '2rem',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        {/* Header - calming, not alarming */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
            {showBreathing ? '🫁' : '💙'}
          </div>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'var(--color-text)',
            marginBottom: '0.5rem'
          }}>
            {showBreathing ? 'Breathe with me' : 'You\'re not alone'}
          </h1>
          <p style={{
            color: 'var(--color-text-muted)',
            fontSize: '0.9rem',
            lineHeight: '1.5'
          }}>
            {showBreathing
              ? 'Focus on your breath. You\'re doing great.'
              : 'It\'s okay to ask for help. People care about you.'
            }
          </p>
        </div>

        {/* Breathing Exercise */}
        {showBreathing ? (
          <div style={{ marginBottom: '1.5rem' }}>
            {/* Breathing circle */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem'
            }}>
              <div style={{
                width: breathPhase === 'inhale' ? '160px' : breathPhase === 'hold' ? '160px' : '100px',
                height: breathPhase === 'inhale' ? '160px' : breathPhase === 'hold' ? '160px' : '100px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
                opacity: 0.3,
                transition: breathPhase === 'exhale' ? 'all 6s ease-out' : 'all 4s ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '80%',
                  height: '80%',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-primary)',
                  opacity: 0.5
                }} />
              </div>

              <p style={{
                marginTop: '1.5rem',
                fontSize: '1.25rem',
                fontWeight: '500',
                color: 'var(--color-primary)',
                textTransform: 'capitalize'
              }}>
                {breathPhase === 'inhale' && 'Breathe in...'}
                {breathPhase === 'hold' && 'Hold...'}
                {breathPhase === 'exhale' && 'Breathe out slowly...'}
              </p>

              {breathCount > 0 && (
                <p style={{
                  marginTop: '0.5rem',
                  fontSize: '0.875rem',
                  color: 'var(--color-text-muted)'
                }}>
                  {breathCount} breath{breathCount !== 1 ? 's' : ''} completed
                </p>
              )}
            </div>

            <button
              onClick={() => {
                setShowBreathing(false)
                setBreathCount(0)
              }}
              className="btn"
              style={{
                width: '100%',
                backgroundColor: 'var(--color-bg-light)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)'
              }}
            >
              I'm ready to continue
            </button>
          </div>
        ) : (
          <>
            {/* Main CTA - Call 988 */}
            <button
              onClick={handleCall988}
              style={{
                width: '100%',
                padding: '1.25rem',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '1rem',
                fontSize: '1.25rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>📞</span>
                Call 988
              </span>
              <span style={{
                fontSize: '0.875rem',
                fontWeight: '400',
                opacity: 0.9
              }}>
                Suicide & Crisis Lifeline - Available 24/7
              </span>
            </button>

            {/* Text option */}
            <a
              href="sms:741741&body=HOME"
              style={{
                display: 'block',
                width: '100%',
                padding: '1rem',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                fontWeight: '500',
                textAlign: 'center',
                textDecoration: 'none',
                marginBottom: '1.5rem'
              }}
            >
              <span style={{ marginRight: '0.5rem' }}>💬</span>
              Text HOME to 741741
            </a>

            {/* Breathing exercise prompt */}
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--color-bg-light)',
              borderRadius: '0.75rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{
                color: 'var(--color-text-muted)',
                fontSize: '0.875rem',
                marginBottom: '0.75rem'
              }}>
                Need a moment to calm down first?
              </p>
              <button
                onClick={() => setShowBreathing(true)}
                className="btn"
                style={{
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem'
                }}
              >
                <span style={{ marginRight: '0.5rem' }}>🫁</span>
                Start Breathing Exercise
              </button>
            </div>

            {/* Other resources */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--color-text)',
                marginBottom: '0.75rem'
              }}>
                More Resources
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {resources
                  .filter(r => r.id !== '988' && r.id !== 'crisis-text')
                  .map(resource => (
                    <a
                      key={resource.id}
                      href={resource.phone ? `tel:${resource.phone}` : resource.url || '#'}
                      target={resource.url ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '0.875rem 1rem',
                        backgroundColor: 'var(--color-bg-light)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '0.5rem',
                        color: 'var(--color-text)',
                        textDecoration: 'none'
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                        {resource.name}
                      </div>
                      {resource.description && (
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--color-text-muted)'
                        }}>
                          {resource.description}
                        </div>
                      )}
                    </a>
                  ))}
              </div>
            </div>

            {/* Grounding reminder */}
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '0.75rem',
              borderLeft: '3px solid var(--color-primary)',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--color-text)',
                fontWeight: '500',
                marginBottom: '0.5rem'
              }}>
                5-4-3-2-1 Grounding
              </p>
              <p style={{
                fontSize: '0.8rem',
                color: 'var(--color-text-muted)',
                lineHeight: '1.5'
              }}>
                Notice 5 things you can see, 4 you can touch, 3 you can hear,
                2 you can smell, and 1 you can taste.
              </p>
            </div>
          </>
        )}

        {/* Back to app */}
        <Link
          to="/"
          style={{
            display: 'block',
            textAlign: 'center',
            color: 'var(--color-text-muted)',
            fontSize: '0.875rem',
            textDecoration: 'none',
            padding: '1rem'
          }}
        >
          ← Back to PresentLens
        </Link>
      </div>
    </div>
  )
}
